/**
 * @deprecated This file is legacy and has been refactored to index-tour.tsx. Use index-tour.tsx instead.
 */
import React, { useCallback, useEffect, useState, useLayoutEffect } from 'react';
import { useTour } from '@reactour/tour';
import { useDispatch, useSelector } from 'react-redux';
import { useEvent } from '@cobuildlab/react-simple-state';
import Tour from '../../../components/tour';
import VideoModal from '../../../components/VideoModal';
import ConfettiModal from '../../../components/ConfettiModal';
import HelpIcon from '../../../assets/images/help-icon.png';
import { getTourCompletionStatus } from './requests';
import { isTourOpenEvent } from '../events';
import { saveCompletedTourAction, toursSlice } from '../tours-slice';
import { getIsAdmin, getIsSuperAdmin } from '../../../utils/users.utils';
import { getAwsRum } from '../../../shared/monitoring';
import { RumEvent } from '../../../shared/analytics/rum';

export const createTourSection = (title, name) => `tour-${title}-${name}`;

export const getTourSection = (title, name) => `#${createTourSection(title, name)}`;

export const toggleTourSectionHighlight = async (step, enabled) => {
  try {
    if (enabled) {
      document.querySelector(step.selector).classList.add('highlight');
      document.querySelector('#tour-lpr-dashboard-total-lpr-scans').classList.add('block-pointer');
    } else {
      document.querySelector(step.selector).classList.remove('highlight');
      document.querySelector('#tour-lpr-dashboard-total-lpr-scans').classList.remove('block-pointer');
    }
  } catch {
    //
  }
};

export const createTour = (title, video, steps, messages, callbacks = {}) => {
  let step;

  function CustomTourProvider({ children, steps, closeAction, skippable = false }) {
    return (
      <Tour steps={steps} closeAction={closeAction} skippable={skippable}>
        {children}
      </Tour>
    );
  }

  function CustomTourConsumer({ children }) {
    const { steps, currentStep, setCurrentStep, setIsOpen } = useTour();

    useEffect(() => {
      setIsOpen(true);
      toggleTourSectionHighlight(steps[0], true);

      return () => {
        steps.forEach((step) => {
          toggleTourSectionHighlight(step, false);
        });
      };
    }, [setIsOpen, steps]);

    useLayoutEffect(() => {
      const skip = () => {
        setCurrentStep(currentStep + 1);
      };

      steps.forEach((step) => {
        toggleTourSectionHighlight(step, false);
      });

      callbacks?.onChange?.(currentStep, skip);
      step = steps[currentStep];
      // This fix the issue with element not being highlighted
      // ensuring the DOM is fully updated before applying the highlight
      requestAnimationFrame(() => toggleTourSectionHighlight(steps[currentStep], true));
    }, [steps, currentStep, setCurrentStep]);

    return <>{children}</>;
  }

  function CustomTourBubble({ setIsOpen }) {
    const id = createTourSection('overview', 'help');
    return (
      <div className="bubble" onClick={() => setIsOpen(true)}>
        <div className="tour-section eye" id={id} data-testid={id}>
          <img src={HelpIcon} alt="Help" />
        </div>
      </div>
    );
  }

  function CustomTourVideo({ closeAction }) {
    return <VideoModal video={video} closeAction={closeAction} />;
  }

  function CustomTourModal({ closeAction }) {
    return (
      <ConfettiModal title={messages.success.title} content={messages.success.content} closeAction={closeAction} />
    );
  }

  function CustomTour({ children, hasVideo = !!video, hidden = false }) {
    const dispatch = useDispatch();
    const isTourOpen = useEvent(isTourOpenEvent);
    const [isEnabled, setIsEnabled] = useState(false);
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const [isPreviouslyCompleted, setIsPreviouslyCompleted] = useState(false);
    const user = useSelector((state) => state.auth.user);
    const { openTourModal } = useSelector((state) => state.toursReducer);

    useEffect(() => {
      getTourCompletionStatus(title)
        .then((isCompleted) => {
          setIsEnabled(!isCompleted);
          setIsPreviouslyCompleted(isCompleted);
        })
        .catch((e) => {
          // Handle error gracefully with logging
          let stringifiedError;
          try {
            stringifiedError = JSON.stringify(e);
          } catch {
            stringifiedError = 'n/a';
          }
          getAwsRum().recordEvent(RumEvent.NETWORK_ERROR, {
            message: 'Error fetching tour completion status',
            title,
            error: e?.message || 'n/a',
            stack: e?.stack || 'n/a',
            string: stringifiedError,
            url: window.location.href,
          });
        });
    }, []);

    const openVideo = () => {
      setIsVideoOpen(true);
    };

    const openTour = () => {
      isTourOpenEvent.dispatch(true);
    };

    const startTour = useCallback(() => {
      if (!isTourOpen && !openTourModal) {
        if (hasVideo) {
          openVideo();
        } else {
          openTour();
        }
      }
    }, [hasVideo, isTourOpen, openTourModal]);

    useEffect(() => {
      if (isEnabled && !isTourOpen && !isVideoOpen && !openTourModal) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        startTour();
      }
    }, [isEnabled, startTour, isTourOpen, isVideoOpen, openTourModal]);

    const closeVideo = () => {
      setIsVideoOpen(false);
      isTourOpenEvent.dispatch(true);
    };

    const openModal = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      dispatch(toursSlice.actions.setOpenTourModal(true));
    };

    const closeModal = () => {
      dispatch(toursSlice.actions.setOpenTourModal(false));
    };

    const saveTourCompletion = useCallback(() => {
      if (user) {
        saveCompletedTourAction({ name: title, userId: user.id }, dispatch);
        setIsEnabled(false);
        setIsPreviouslyCompleted(true);
      }
    }, [user, dispatch]);

    const closeTour = (hasFinishedTour) => {
      isTourOpenEvent.dispatch(false);
      setIsEnabled(false);

      if (hasFinishedTour && messages?.success) {
        openModal();
      }

      callbacks?.onFinish?.();
      if (callbacks?.setSaveCondition) {
        const canSave = callbacks?.setSaveCondition(step);

        if (canSave) {
          saveTourCompletion();
        }
      } else {
        saveTourCompletion();
      }
    };

    return (
      <CustomTourProvider steps={steps} closeAction={closeTour} skippable={isPreviouslyCompleted}>
        {!hidden && <CustomTourBubble setIsOpen={startTour} />}
        {children}
        {isTourOpen && <CustomTourConsumer setIsOpen={openTour} />}
        {isVideoOpen && <CustomTourVideo closeAction={closeVideo} />}
        {openTourModal && <CustomTourModal closeAction={closeModal} />}
      </CustomTourProvider>
    );
  }

  return CustomTour;
};

export const doArrow = (position, verticalAlign, horizontalAlign) => {
  const oppositeSide = {
    top: 'bottom',
    bottom: 'top',
    right: 'left',
    left: 'right',
  };

  if (!position || position === 'custom') {
    return {};
  }

  const width = 16;
  const height = 12;
  const color = '#292932';
  const isVertical = position === 'top' || position === 'bottom';
  const spaceFromSide = 10;
  const obj = {
    [isVertical ? 'borderLeft' : 'borderTop']: `${width / 2}px solid transparent`, // CSS Triangle width
    [isVertical ? 'borderRight' : 'borderBottom']: `${width / 2}px solid transparent`, // CSS Triangle width
    [`border${position[0].toUpperCase()}${position.substring(1)}`]: `${height}px solid ${color}`, // CSS Triangle height
    [isVertical ? oppositeSide[horizontalAlign] : verticalAlign]: height + spaceFromSide, // space from side
    [oppositeSide[position]]: -height + 2,
  };

  return {
    '&::after': {
      content: "''",
      width: 0,
      height: 0,
      position: 'absolute',
      ...obj,
    },
  };
};

/**
 * @deprecated Use useIsInAdminGroup from 'src/modules/overview/shared/useIsAdmin' instead.
 */
export const useIsAdminOrSuper = () => {
  const user = useSelector((state) => state?.auth?.user);
  const isAdmin = getIsAdmin(user) || getIsSuperAdmin(user);

  return isAdmin;
};
